/**
 * PUT a File to a signed URL with byte-level progress.
 *
 * `fetch` does not expose upload progress for request bodies, so this uses
 * XMLHttpRequest's `upload.onprogress` (loaded / total) instead.
 */

export type PutProgressHandler = (percent: number) => void;

export async function putFileWithProgress(
  url: string,
  file: File | Blob,
  options: {
    contentType: string;
    onProgress?: PutProgressHandler;
  },
): Promise<{ ok: boolean; status: number }> {
  const { contentType, onProgress } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('content-type', contentType);

    xhr.upload.onprogress = (event) => {
      if (!onProgress) return;
      if (!event.lengthComputable || event.total <= 0) return;
      const pct = Math.min(100, Math.round((event.loaded / event.total) * 100));
      onProgress(pct);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
      }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status });
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new Error('Upload aborted'));

    xhr.send(file);
  });
}
